import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Send as SendIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import axios from "../../api/axios";
import { useAuth } from "../../contexts/AuthContext";
import { endpoints } from "../../api/endpoint";
import FeedbackStatusBar from "./FeedbackStatusBar";

const BRAND = {
  primary: "#0E7A6E",
  dark: "#0B5F56",
  tint: "#E6F5F2",
};

const formatTime = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay) return time;

  const dateLabel = date.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
  });
  return `${dateLabel}, ${time}`;
};

const initials = (name = "") =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const currentUserStyle = {
  bg: BRAND.primary,
  text: "#FFFFFF",
  timeText: "rgba(255,255,255,0.72)",
  badgeBg: BRAND.dark,
  badgeColor: "#FFFFFF",
};

const otherUserStyle = {
  bg: "#F3F5F4",
  text: "#232E2B",
  timeText: "#98A6A0",
  badgeBg: "#E6ECEA",
  badgeColor: "#3A4441",
};

const CommentBubble = ({ comment }) => {
  const { userId: currentUserId } = useAuth();

  const isSender = comment.userId === currentUserId;
  const style = isSender ? currentUserStyle : otherUserStyle;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isSender ? "row-reverse" : "row",
        alignItems: "flex-end",
        gap: 1,
        mb: 1.75,
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: style.badgeBg,
          color: style.badgeColor,
          flexShrink: 0,
        }}
      >
        {initials(comment.displayName || comment.username)}
      </Avatar>

      <Box sx={{ maxWidth: "76%", minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 0.35,
            flexDirection: isSender ? "row-reverse" : "row",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#12181A" }}>
            {isSender ? "You" : comment.displayName || comment.username}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: "#98A6A0" }}>
            {formatTime(comment.createdAt)}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: style.bg,
            borderRadius: "16px",
            ...(isSender
              ? { borderBottomRightRadius: "4px" }
              : { borderBottomLeftRadius: "4px" }),
            px: 1.75,
            py: 1,
            boxShadow: isSender
              ? "0 1px 2px rgba(11,95,86,0.18)"
              : "0 1px 2px rgba(16,24,22,0.05)",
          }}
        >
          <Typography
            sx={{
              fontSize: 13.5,
              lineHeight: 1.45,
              color: style.text,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {comment.message}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const LeadFeedback = ({ leadId, maxHeight = 320, refetchTableData }) => {
  const { user, isAdmin, userId } = useAuth();

  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");

  const [feedbackStatus, setFeedbackStatus] = useState("OPEN");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const scrollRef = useRef(null);

  const fetchFeedback = useCallback(async () => {
    if (!leadId) return;

    setIsLoading(true);
    setError(null);

    try {
      const res = await axios.get(
        `${endpoints.getDriverLeadFeedback}/${leadId}`,
        { params: { limit: 100, offset: 0 } },
      );
      setComments(res.data?.data || []);
      setFeedbackStatus(res.data?.feedbackStatus);
    } catch (err) {
      setError("Couldn't load feedback. Try refreshing.");
    } finally {
      setIsLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchFeedback();
  }, [leadId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  const handleSubmit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isSubmitting || !leadId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const res = await axios.post(
        `${endpoints.addDriverLeadFeedback}/${leadId}`,
        {
          message: trimmed,
          username: user?.email || "Unknown",
          userId: user?.sub || null,
          displayName: user?.name,
        },
      );

      setComments((prev) => [...prev, res.data]);
      setDraft("");
    } catch (err) {
      setError("Couldn't send that comment. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleResolve = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(`${endpoints.resolveDriverLeadFeedback}/${leadId}`, {
        status: newStatus,
      });
      setFeedbackStatus(newStatus);
      refetchTableData();
    } catch (err) {
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid #E6ECEA",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "#fff",
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #E6ECEA",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: "#12181A" }}
            >
              Feedback
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#66766F" }}>
              Notes between dealer and BP on this lead
            </Typography>
          </Box>

          <Box
            component="button"
            onClick={fetchFeedback}
            disabled={isLoading}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 30,
              border: "1px solid #E6ECEA",
              borderRadius: "50%",
              backgroundColor: "transparent",
              color: "#66766F",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: BRAND.primary,
                color: BRAND.dark,
                backgroundColor: BRAND.tint,
              },
              "&:disabled": { opacity: 0.5, cursor: "default" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>

        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight,
            overflowY: "auto",
            px: 2,
            py: 1.5,
            backgroundColor: "#FAFCFB",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#D6DEDB",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#B8C4C0" },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} sx={{ color: BRAND.primary }} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ fontSize: 13, color: "#B3261E" }}>
                {error}
              </Typography>
            </Box>
          ) : comments.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ fontSize: 13, color: "#98A6A0" }}>
                No feedback yet.{" "}
                {isAdmin
                  ? "Add a note for the dealer."
                  : "You'll see admin notes here."}
              </Typography>
            </Box>
          ) : (
            comments.map((comment) => (
              <CommentBubble key={comment.id} comment={comment} />
            ))
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderTop: "1px solid #E6ECEA",
            backgroundColor: "#fff",
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={
              isAdmin ? "Leave feedback for the dealer…" : "Add a comment…"
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                fontSize: 13.5,
                backgroundColor: "#F3F5F4",
                "& fieldset": { border: "none" },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                  boxShadow: `0 0 0 1.5px ${BRAND.primary}`,
                },
              },
            }}
          />
          <Button
            onClick={handleSubmit}
            disabled={!draft.trim() || isSubmitting}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: BRAND.primary,
              color: "#fff",
              flexShrink: 0,
              transition: "all 0.15s ease",
              "&:hover": { backgroundColor: BRAND.dark },
              "&.Mui-disabled": {
                backgroundColor: "#E6ECEA",
                color: "#98A6A0",
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <SendIcon fontSize="small" />
            )}
          </Button>
        </Box>
      </Box>

      {isAdmin && (
        <FeedbackStatusBar
          status={feedbackStatus}
          isAdmin={isAdmin}
          onResolve={handleResolve}
          isUpdating={isUpdatingStatus}
        />
      )}
    </>
  );
};

export default LeadFeedback;
